import { AccountSelector, Alert, AnimatedContentLoader, Button, Prompt, Select } from '@components'
import { PermissionSet, useBusinessContext, usePermission, usePermissionsLoaded } from '@context'
import { fetchBankIntegrations, importDocuments } from '@query'
import { fetchHolviAccounts } from '@query/holvi'
import { normalizeDateString, normalizeNumberString } from '@utils'
import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaFeatherAlt } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import {
  AnimatedRow,
  ButtonContent,
  ButtonCount,
  ButtonText,
  CellValue,
  Footer,
  PageContent,
  StyledHeader,
  StyledTable
} from './DocumentImportTable.styled'
import { ACC_TYPES_PURCHASE_PAYMENT, ACC_TYPES_SALES_PAYMENT } from '@root/constants'

type JSONObject = { [key: string]: any }

interface Props {
  data: JSONObject[]
  onClose: () => void
}

const NONE_FIELD = 'none'
const DOCUMENT_FIELD_TYPES = [
  {
    label: '-',
    value: NONE_FIELD
  },
  {
    label: 'Päivämäärä',
    value: 'date'
  },
  {
    label: 'Kuvaus',
    value: 'description'
  },
  {
    label: 'Summa',
    value: 'amount'
  },
  {
    label: 'Kontakti',
    value: 'contact_hint'
  },
  {
    label: 'Arkistointitunnus',
    value: 'identifier'
  },
  {
    label: 'Viite',
    value: 'reference_number'
  }
]

interface DocumentFieldSelectorProps {
  value: string
  onChange: (value) => void
}

const DocumentFieldSelector: React.FC<DocumentFieldSelectorProps> = ({ onChange, value }) => {
  const [t] = useTranslation()

  return (
    <Select
      label={t('document.import.fieldType')}
      onChange={e => onChange(e.currentTarget.value)}
      value={value}
    >
      {DOCUMENT_FIELD_TYPES.map(({ label, value }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </Select>
  )
}

const checkIfReady = (dataTypes: string[]) => {
  return (
    dataTypes.indexOf('date') !== -1 &&
    dataTypes.indexOf('description') !== -1 &&
    dataTypes.indexOf('amount') !== -1
  )
}

export const DocumentImportTable: React.FC<Props> = ({ data, onClose }) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const permissionsLoaded = usePermissionsLoaded()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const initialColNames = useMemo(() => Object.keys(data[0]), [data])
  const [dataTypes, setDataTypes] = useState<string[]>(initialColNames.map(() => NONE_FIELD))
  const [targetAccount, setTargetAccount] = useState<number>()
  const [pendingLinkedAccountId, setPendingLinkedAccountId] = useState<number>()
  const queryClient = useQueryClient()

  const { data: bankIntegrationsData, isLoading: isBankIntegrationsLoading } = useQuery({
    queryKey: [slug, 'bank_integrations'],
    queryFn: () => fetchBankIntegrations({ slug }),
    enabled: canManageIntegrations
  })
  const { data: holviAccountsData, isLoading: isHolviAccountsLoading } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    enabled: canManageIntegrations && Boolean(business?.holvi_bookkeeping_api_enabled)
  })
  const isLinkedAccountsLoading =
    !permissionsLoaded || isBankIntegrationsLoading || isHolviAccountsLoading
  const linkedAccountIds = useMemo(() => {
    const accountIds = new Set<number>()
    for (const integration of bankIntegrationsData?.results || []) {
      if (!integration.enabled) continue
      for (const account of integration.accounts || []) {
        if (account.enabled && account.account_id) {
          accountIds.add(account.account_id)
        }
      }
    }
    for (const account of holviAccountsData?.results || []) {
      if (account.enabled && account.account) {
        accountIds.add(account.account)
      }
    }
    return accountIds
  }, [bankIntegrationsData, holviAccountsData])

  const handleTargetAccountChange = (accountId: number) => {
    if (accountId && linkedAccountIds.has(accountId) && accountId !== targetAccount) {
      setPendingLinkedAccountId(accountId)
      return
    }
    setTargetAccount(accountId)
  }

  const isReady = React.useMemo(
    () => Boolean(targetAccount) && checkIfReady(dataTypes),
    [dataTypes, targetAccount]
  )

  const formatByKey = (value, dataType): [string, boolean] => {
    switch (dataType) {
      case 'date': {
        return normalizeDateString(value)
      }

      case 'amount': {
        return normalizeNumberString(value)
      }

      case 'reference_number': {
        return value ? [value, false] : [undefined, false]
      }

      default: {
        return [value, false]
      }
    }
  }

  const RenderedValue = React.memo<{ value: any; dataType: string }>(({ value, dataType }) => {
    switch (dataType) {
      case 'date': {
        const [formatted, error] = formatByKey(value, dataType)
        return (
          <CellValue
            value={formatted}
            originalValue={value}
            error={error ? t('document.import.invalidDate') : undefined}
          />
        )
      }

      case 'amount': {
        const [formatted, error] = formatByKey(value, dataType)
        return (
          <CellValue
            value={formatted}
            originalValue={value}
            error={error ? t('document.import.invalidAmount') : undefined}
          />
        )
      }

      case 'none': {
        return <CellValue value={value} isUntouched={true} />
      }

      default: {
        return <CellValue value={value} />
      }
    }
  })

  const columns = React.useMemo<ColumnDef<JSONObject>[]>(
    () =>
      initialColNames.map((name, index) => ({
        id: name || `column-${index}`,
        header: () => (
          <DocumentFieldSelector
            value={dataTypes[index]}
            onChange={value =>
              setDataTypes(types => {
                const newTypes = [...types]
                newTypes[index] = value
                return newTypes
              })
            }
          />
        ),
        accessorFn: row => row[name],
        cell: info => <RenderedValue value={info.getValue()} dataType={dataTypes[index]} />,
        size: 240,
        minSize: 200,
        maxSize: 500
      })),
    [dataTypes, initialColNames, RenderedValue]
  )

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 240,
      minSize: 200
    },
    getCoreRowModel: getCoreRowModel()
  })

  const generateFinalData = (rows: JSONObject[], columnNames: string[], types: string[]) => {
    const mapper: Record<string, string> = {}
    columnNames.forEach((accessor, index) => {
      const dataType = types[index]
      mapper[accessor] = dataType
    })

    return rows.map(row => {
      const items = Object.entries(row)
      const mappedRow: Record<string, any> = {}

      items.forEach(([key, value]) => {
        const newKey = mapper[key]
        if (!newKey || newKey === NONE_FIELD) {
          return
        }

        const [formatted, error] = formatByKey(value, newKey)

        if (error) {
          throw new Error('Invalid data')
        }

        mappedRow[newKey] = formatted
      })

      return mappedRow
    })
  }

  const { mutateAsync: importDocumentsMutation, isPending: isLoading } = useMutation<
    unknown,
    unknown,
    any
  >({
    mutationFn: finalData => importDocuments({ slug, accountId: targetAccount, data: finalData }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        onClose()
      },
      onError: error => {
        const { data } = (error as any) ?? {}
        if ('period_missing' in data) {
          toast.error(t('document.import.missingPeriod'))
        }
      }
    }
  })

  const handleClick = () => {
    try {
      const finalData = generateFinalData(data, initialColNames, dataTypes)
      toast.promise(importDocumentsMutation(finalData), {
        loading: t('document.import.loading'),
        success: t('document.import.success'),
        error: t('document.import.importError')
      })
    } catch {
      toast.error(t('document.import.invalidData'))
    }
  }

  return (
    <AnimatedContentLoader isLoading={isLinkedAccountsLoading}>
      <PageContent>
        <StyledHeader>
          <Alert
            title={t('document.import.alertTitle')}
            description={t('document.import.alertContent')}
          />
          <AccountSelector
            label={t('document.import.targetAccountLabel')}
            info={t('document.import.targetAccountHint')}
            value={targetAccount}
            onChange={handleTargetAccountChange}
            accountFilter={{ type: [...ACC_TYPES_PURCHASE_PAYMENT, ...ACC_TYPES_SALES_PAYMENT] }}
          />
        </StyledHeader>

        <StyledTable>
          <div style={{ width: '100%' }} className="table">
            <div className="header">
              {table.getHeaderGroups().map(headerGroup => (
                <div key={headerGroup.id} className="tr">
                  {headerGroup.headers.map(header => (
                    <div
                      key={header.id}
                      className="th"
                      style={{
                        flex: `1 0 ${header.getSize()}px`,
                        width: `${header.getSize()}px`
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div>
              {table.getRowModel().rows.map(row => (
                <AnimatedRow key={row.id} className="tr">
                  {row.getVisibleCells().map(cell => (
                    <div
                      key={cell.id}
                      className="td"
                      style={{
                        flex: `1 0 ${cell.column.getSize()}px`,
                        width: `${cell.column.getSize()}px`
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ))}
                </AnimatedRow>
              ))}
            </div>
          </div>
        </StyledTable>
        <Footer>
          <Button icon={<FaArrowLeft />} onClick={onClose} intent="default">
            {t('general.cancel')}
          </Button>

          <div style={{ flex: 1 }} />

          <Button
            icon={<FaFeatherAlt />}
            intent="primary"
            showSpinner={isLoading}
            disabled={!isReady || isLoading}
            onClick={handleClick}
          >
            <ButtonContent>
              <ButtonText>{t('document.importDocuments')}</ButtonText>
              <ButtonCount>{data.length}</ButtonCount>
            </ButtonContent>
          </Button>
        </Footer>
        <Prompt
          isVisible={Boolean(pendingLinkedAccountId)}
          title={t('document.import.linkedAccountConfirmTitle')}
          description={t('document.import.linkedAccountConfirmDescription')}
          onClose={() => setPendingLinkedAccountId(undefined)}
          buttons={[
            {
              text: t('general.cancel'),
              intent: 'default',
              action: () => setPendingLinkedAccountId(undefined)
            },
            {
              text: t('general.yes'),
              intent: 'primary',
              action: () => {
                setTargetAccount(pendingLinkedAccountId)
                setPendingLinkedAccountId(undefined)
              }
            }
          ]}
        />
      </PageContent>
    </AnimatedContentLoader>
  )
}
